import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent } from "vue";
const _hoisted_1 = {
  key: 1,
  class: "flex flex-col lg:flex-row md:flex-row sm:flex-col flex-1 min-h-0"
};
const _hoisted_2 = {
  class: "bg-slate-100 p-6 lg:w-1/2 md:w-3/5 sm:w-full overflow-auto"
};
const _hoisted_3 = {
  class: "bg-white p-8 text-gray-800 rounded-sm"
};
const _hoisted_4 = {
  class: "flex justify-between text-sm pb-6 border-b border-slate-200"
};
const _hoisted_5 = {
  class: "bg-green-100 px-2 py-0.5 rounded"
};
const _hoisted_6 = ["innerHTML"];
const _hoisted_7 = {
  class: "border-y border-slate-200 text-base"
};
const _hoisted_8 = {
  class: "col-span-9"
};
const _hoisted_9 = {
  class: "col-span-3 text-right bg-green-100 rounded px-2"
};
const _hoisted_10 = ["innerHTML"];
const _hoisted_11 = {
  class: "grid grid-cols-2 gap-6 text-sm pt-6"
};
const _hoisted_12 = {
  class: "pt-1"
};
const _hoisted_13 = {
  class: "pt-1"
};
const _hoisted_14 = {
  class: "text-xs text-slate-500 pt-6"
};
const _hoisted_15 = {
  class: "pl-3 pt-0 lg:w-1/2 md:w-[40%] sm:w-full bg-white flex flex-col border border-slate-200 rounded-sm min-h-0"
};
const _hoisted_16 = {
  class: "flex-1 overflow-auto px-3 py-3"
};
const _hoisted_17 = {
  key: 0,
  class: "mb-3 rounded border border-orange-300 bg-orange-50 px-3 py-2 text-sm text-orange-700"
};
const _hoisted_18 = {
  class: "mb-3"
};
const _hoisted_19 = {
  class: "mb-3"
};
const _hoisted_20 = {
  class: "pb-3"
};
const _hoisted_21 = {
  class: "flex justify-end gap-2 border-t border-slate-200 px-3 py-3 flex-shrink-0"
};
import { computed, onBeforeUnmount, onMounted, reactive } from 'vue';
import { EditorContent, useEditor } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import { useInvoiceTemplateStore } from '@/services/useInvoiceTemplateStore.js';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import Loader from '@/components/Loader.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import SettlementEditorSection from './SettlementEditorSection.vue';
import { SETTLEMENT_PREVIEW_META, SETTLEMENT_PREVIEW_SAMPLE_VALUES, SETTLEMENT_V1_KEYWORDS } from '@/constants/settlementKeywordConstants';
import { enrichBackendKeywordsWithMetadata, findUnsupportedSettlementKeywords, renderSettlementTemplateWithSamples } from '@/utils/keywords';
import { sanitizeEditorHtml, normalizeEditorValue } from '@/utils/editorHtml';
import SETTLEMENT_EDITOR_CLASS from '@/utils/editorClass';
export default {
  __name: 'UpdateSettlementStatementTemplatePage',
  props: {
    alerts: {
      type: Object,
      required: true
    }
  },
  emits: ['closeDialog'],
  setup(__props, {
    emit: __emit
  }) {
    const emit = __emit;
    const invoiceTemplateStore = useInvoiceTemplateStore();
    const {
      handleSuccess,
      handleError
    } = useSnackbarHandler();
    const sampleValues = SETTLEMENT_PREVIEW_SAMPLE_VALUES;
    const previewMeta = SETTLEMENT_PREVIEW_META;
    const notifySuccess = message => {
      handleSuccess(message);
    };
    const notifyError = messageOrError => {
      handleError(messageOrError);
    };
    const templateState = reactive({
      loading: true,
      saving: false,
      id: 'settlement-statement-template',
      invoiceTemplateId: 'settlement-statement-template',
      enableOpeningParagraph: true,
      openingParagraph: '<p></p>',
      enableClosingParagraph: true,
      closingParagraph: '<p></p>',
      jurisdictionNote: '',
      keywords: []
    });
    const buildEditor = onUpdate => useEditor({
      extensions: [StarterKit.configure({
        blockquote: false,
        code: false,
        codeBlock: false,
        hardBreak: false,
        heading: false,
        horizontalRule: false,
        orderedList: false,
        strike: false,
        history: true
      })],
      content: '<p></p>',
      editorProps: {
        attributes: {
          class: SETTLEMENT_EDITOR_CLASS
        },
        transformPastedHTML: html => sanitizeEditorHtml(html)
      },
      onUpdate
    });
    const openingEditor = buildEditor(({
      editor
    }) => {
      templateState.openingParagraph = editor.getHTML();
    });
    const closingEditor = buildEditor(({
      editor
    }) => {
      templateState.closingParagraph = editor.getHTML();
    });
    const openingPreviewHtml = computed(() => renderSettlementTemplateWithSamples(sanitizeEditorHtml(templateState.openingParagraph)));
    const closingPreviewHtml = computed(() => renderSettlementTemplateWithSamples(sanitizeEditorHtml(templateState.closingParagraph)));
    const keywordGroups = computed(() => {
      const groups = new Map();

      // Use enriched keywords from API, fall back to local keywords
      const keywords = templateState.keywords.length > 0 ? templateState.keywords : SETTLEMENT_V1_KEYWORDS;
      keywords.forEach(keyword => {
        const groupName = keyword.group || 'General';
        if (!groups.has(groupName)) {
          groups.set(groupName, []);
        }
        groups.get(groupName).push(keyword);
      });
      return Array.from(groups.entries()).map(([group, keywords]) => ({
        group,
        keywords
      }));
    });
    const unsupportedKeywords = computed(() => {
      const tokens = [...findUnsupportedSettlementKeywords(templateState.openingParagraph), ...findUnsupportedSettlementKeywords(templateState.closingParagraph)];
      return tokens.filter((token, index) => tokens.indexOf(token) === index);
    });
    const syncEditorsFromState = () => {
      if (openingEditor.value) {
        openingEditor.value.commands.setContent(templateState.openingParagraph, false);
      }
      if (closingEditor.value) {
        closingEditor.value.commands.setContent(templateState.closingParagraph, false);
      }
    };
    const hydrateTemplateState = template => {
      templateState.id = template.id || 'settlement-statement-template';
      templateState.invoiceTemplateId = template.invoiceTemplateId || 'settlement-statement-template';
      templateState.enableOpeningParagraph = template.enableOpeningParagraph !== false;
      templateState.openingParagraph = normalizeEditorValue(template.openingParagraph);
      templateState.enableClosingParagraph = template.enableClosingParagraph !== false;
      templateState.closingParagraph = normalizeEditorValue(template.closingParagraph);
      templateState.jurisdictionNote = template.jurisdictionNote || '';
      syncEditorsFromState();
    };
    const loadTemplate = async () => {
      templateState.loading = true;
      try {
        const template = await invoiceTemplateStore.getSettlementStatementTemplate();
        hydrateTemplateState(template || {});
      } finally {
        templateState.loading = false;
      }
    };
    const loadKeywords = async () => {
      try {
        const backendKeywords = await invoiceTemplateStore.getSettlementStatementKeywords();
        const enrichedKeywords = enrichBackendKeywordsWithMetadata(backendKeywords);
        templateState.keywords = enrichedKeywords;
      } catch (error) {
        templateState.keywords = [];
      }
    };
    onMounted(async () => {
      await Promise.all([loadTemplate(), loadKeywords()]);
    });
    onBeforeUnmount(() => {
      openingEditor.value?.destroy();
      closingEditor.value?.destroy();
    });
    const resolveEditor = editorRefOrInstance => {
      if (!editorRefOrInstance) {
        return null;
      }
      return editorRefOrInstance.value || editorRefOrInstance;
    };
    const insertKeyword = async (editorRefOrInstance, token) => {
      const editor = resolveEditor(editorRefOrInstance);
      if (!editor) {
        return;
      }
      editor.chain().focus().insertContent(` ${token} `).run();
    };
    const isMarkActive = (editorRefOrInstance, mark) => {
      const editor = resolveEditor(editorRefOrInstance);
      if (!editor) {
        return false;
      }
      return editor.isActive(mark);
    };
    const isNodeActive = (editorRefOrInstance, node) => {
      const editor = resolveEditor(editorRefOrInstance);
      if (!editor) {
        return false;
      }
      return editor.isActive(node);
    };
    const toggleMark = (editorRef, markType) => {
      const editor = resolveEditor(editorRef);
      if (!editor) {
        return;
      }
      const chain = editor.chain().focus();
      if (markType === 'bold') {
        chain.toggleBold().run();
        return;
      }
      if (markType === 'italic') {
        chain.toggleItalic().run();
      }
    };
    const toggleBulletList = editorRef => {
      const editor = resolveEditor(editorRef);
      if (!editor) {
        return;
      }
      editor.chain().focus().toggleBulletList().run();
    };
    const undo = editorRef => {
      const editor = resolveEditor(editorRef);
      if (!editor) {
        return;
      }
      editor.chain().focus().undo().run();
    };
    const redo = editorRef => {
      const editor = resolveEditor(editorRef);
      if (!editor) {
        return;
      }
      editor.chain().focus().redo().run();
    };
    const cancel = () => {
      hydrateTemplateState(invoiceTemplateStore.settlementStatementTemplate || {});
      emit('closeDialog', false);
    };
    const saveTemplate = async () => {
      templateState.saving = true;
      if (unsupportedKeywords.value.length) {
        notifyError(`Remove unsupported keywords: ${unsupportedKeywords.value.join(', ')}`);
        templateState.saving = false;
        return;
      }
      try {
        await invoiceTemplateStore.updateSettlementStatementTemplate({
          id: templateState.id,
          invoiceTemplateId: templateState.invoiceTemplateId,
          name: 'Settlement Statement',
          settlementStatementPage: true,
          enableOpeningParagraph: templateState.enableOpeningParagraph,
          openingParagraph: sanitizeEditorHtml(templateState.openingParagraph),
          enableClosingParagraph: templateState.enableClosingParagraph,
          closingParagraph: sanitizeEditorHtml(templateState.closingParagraph),
          jurisdictionNote: templateState.jurisdictionNote
        });
        notifySuccess('Settlement statement template saved successfully.');
        emit('closeDialog', true);
      } catch (error) {
        if (error?.response?.status === 422) {
          notifyError('Settlement statement content exceeds the character limit.');
          return;
        }
        if (error?.response?.status === 403) {
          notifyError('Only firm admins can update this template.');
          return;
        }
        notifyError(error?.response?.data?.message || error?.message || 'Error saving settlement statement template.');
      } finally {
        templateState.saving = false;
      }
    };
    return (_ctx, _cache) => {
      const _component_v_text_field = _resolveComponent("v-text-field");
      const _component_v_card = _resolveComponent("v-card");
      return _openBlock(), _createBlock(_component_v_card, {
        class: "p-3 overflow-hidden bg-gray-200 rounded-lg h-screen flex flex-col"
      }, {
        default: _withCtx(() => [templateState.loading ? (_openBlock(), _createBlock(Loader, {
          key: 0,
          what: "Settlement Statement",
          class: "my-8"
        })) : (_openBlock(), _createElementBlock("div", _hoisted_1, [_createElementVNode("div", _hoisted_2, [_createElementVNode("div", _hoisted_3, [_cache[20] || (_cache[20] = _createElementVNode("div", {
          class: "text-xs tracking-[0.28em] text-center text-slate-500 font-semibold pb-6"
        }, " SETTLEMENT STATEMENT ", -1)), _createElementVNode("div", _hoisted_4, [_createElementVNode("div", null, [_cache[15] || (_cache[15] = _createTextVNode(" Matter: ", -1)), _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(sampleValues).matter_name), 1)]), _createElementVNode("div", null, "Date: " + _toDisplayString(_unref(previewMeta).statementDate), 1)]), templateState.enableOpeningParagraph ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "text-base leading-8 py-6 border-b border-slate-200",
          innerHTML: openingPreviewHtml.value
        }, null, 8, _hoisted_6)) : _createCommentVNode("", true), _cache[21] || (_cache[21] = _createElementVNode("div", {
          class: "text-xs uppercase tracking-wider text-slate-500 font-semibold pt-6 pb-2"
        }, " Description ", -1)), _createElementVNode("div", _hoisted_7, [(_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(previewMeta).rows, row => {
          return _openBlock(), _createElementBlock("div", {
            key: row.label,
            class: _normalizeClass(['grid grid-cols-12 py-2', row.bold ? 'font-semibold' : 'border-b border-slate-200'])
          }, [_createElementVNode("div", _hoisted_8, _toDisplayString(row.label), 1), _createElementVNode("div", _hoisted_9, _toDisplayString(_unref(sampleValues)[row.valueKey]), 1)], 2);
        }), 128))]), templateState.enableClosingParagraph ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "text-base leading-8 py-6",
          innerHTML: closingPreviewHtml.value
        }, null, 8, _hoisted_10)) : _createCommentVNode("", true), _createElementVNode("div", _hoisted_11, [_createElementVNode("div", null, [_cache[16] || (_cache[16] = _createElementVNode("div", {
          class: "border-b border-slate-500 pb-1"
        }, null, -1)), _createElementVNode("div", _hoisted_12, " Attorney signature - " + _toDisplayString(_unref(sampleValues).attorney_name), 1)]), _cache[18] || (_cache[18] = _createElementVNode("div", null, [_createElementVNode("div", {
          class: "border-b border-slate-500 pb-1"
        }), _createElementVNode("div", {
          class: "pt-1"
        }, "Date")], -1)), _createElementVNode("div", null, [_cache[17] || (_cache[17] = _createElementVNode("div", {
          class: "border-b border-slate-500 pb-1"
        }, null, -1)), _createElementVNode("div", _hoisted_13, " Client signature - " + _toDisplayString(_unref(sampleValues).client_name), 1)]), _cache[19] || (_cache[19] = _createElementVNode("div", null, [_createElementVNode("div", {
          class: "border-b border-slate-500 pb-1"
        }), _createElementVNode("div", {
          class: "pt-1"
        }, "Date")], -1))]), _createElementVNode("div", _hoisted_14, _toDisplayString(templateState.jurisdictionNote), 1)])]), _createElementVNode("div", _hoisted_15, [_cache[26] || (_cache[26] = _createElementVNode("div", {
          class: "text-base font-semibold px-3 py-3 border-b border-slate-200"
        }, "Template content", -1)), _createElementVNode("div", _hoisted_16, [unsupportedKeywords.value.length ? (_openBlock(), _createElementBlock("div", _hoisted_17, " Unsupported keywords: " + _toDisplayString(unsupportedKeywords.value.join(', ')), 1)) : _createCommentVNode("", true), _createElementVNode("div", _hoisted_18, [_createVNode(SettlementEditorSection, {
          title: "Opening paragraph",
          enabled: templateState.enableOpeningParagraph,
          "keyword-groups": keywordGroups.value,
          "bold-active": isMarkActive(_unref(openingEditor), 'bold'),
          "italic-active": isMarkActive(_unref(openingEditor), 'italic'),
          "bullet-list-active": isNodeActive(_unref(openingEditor), 'bulletList'),
          "onUpdate:enabled": _cache[0] || (_cache[0] = $event => templateState.enableOpeningParagraph = $event),
          onToggleBold: _cache[1] || (_cache[1] = $event => toggleMark(_unref(openingEditor), 'bold')),
          onToggleItalic: _cache[2] || (_cache[2] = $event => toggleMark(_unref(openingEditor), 'italic')),
          onToggleBulletList: _cache[3] || (_cache[3] = $event => toggleBulletList(_unref(openingEditor))),
          onUndo: _cache[4] || (_cache[4] = $event => undo(_unref(openingEditor))),
          onRedo: _cache[5] || (_cache[5] = $event => redo(_unref(openingEditor))),
          onInsertKeyword: _cache[6] || (_cache[6] = $event => insertKeyword(_unref(openingEditor), $event))
        }, {
          default: _withCtx(() => [_unref(openingEditor) ? (_openBlock(), _createBlock(_unref(EditorContent), {
            key: 0,
            editor: _unref(openingEditor)
          }, null, 8, ["editor"])) : _createCommentVNode("", true)]),
          _: 1
        }, 8, ["enabled", "keyword-groups", "bold-active", "italic-active", "bullet-list-active"])]), _createElementVNode("div", _hoisted_19, [_createVNode(SettlementEditorSection, {
          title: "Disclosures & closing",
          enabled: templateState.enableClosingParagraph,
          "keyword-groups": keywordGroups.value,
          "bold-active": isMarkActive(_unref(closingEditor), 'bold'),
          "italic-active": isMarkActive(_unref(closingEditor), 'italic'),
          "bullet-list-active": isNodeActive(_unref(closingEditor), 'bulletList'),
          "onUpdate:enabled": _cache[7] || (_cache[7] = $event => templateState.enableClosingParagraph = $event),
          onToggleBold: _cache[8] || (_cache[8] = $event => toggleMark(_unref(closingEditor), 'bold')),
          onToggleItalic: _cache[9] || (_cache[9] = $event => toggleMark(_unref(closingEditor), 'italic')),
          onToggleBulletList: _cache[10] || (_cache[10] = $event => toggleBulletList(_unref(closingEditor))),
          onUndo: _cache[11] || (_cache[11] = $event => undo(_unref(closingEditor))),
          onRedo: _cache[12] || (_cache[12] = $event => redo(_unref(closingEditor))),
          onInsertKeyword: _cache[13] || (_cache[13] = $event => insertKeyword(_unref(closingEditor), $event))
        }, {
          default: _withCtx(() => [_unref(closingEditor) ? (_openBlock(), _createBlock(_unref(EditorContent), {
            key: 0,
            editor: _unref(closingEditor)
          }, null, 8, ["editor"])) : _createCommentVNode("", true)]),
          _: 1
        }, 8, ["enabled", "keyword-groups", "bold-active", "italic-active", "bullet-list-active"])]), _createElementVNode("div", _hoisted_20, [_cache[22] || (_cache[22] = _createElementVNode("div", {
          class: "text-base font-semibold pb-1"
        }, "Jurisdiction note", -1)), _createVNode(_component_v_text_field, {
          modelValue: templateState.jurisdictionNote,
          "onUpdate:modelValue": _cache[14] || (_cache[14] = $event => templateState.jurisdictionNote = $event),
          density: "comfortable",
          variant: "outlined",
          "hide-details": "auto"
        }, null, 8, ["modelValue"]), _cache[23] || (_cache[23] = _createElementVNode("div", {
          class: "text-xs text-slate-500 pt-1"
        }, " Informational only - LeanLaw does not enforce jurisdictional rules. ", -1))])]), _createElementVNode("div", _hoisted_21, [_createVNode(LlSecondaryButton, {
          variant: "outlined",
          onBtnClick: cancel
        }, {
          default: _withCtx(() => [...(_cache[24] || (_cache[24] = [_createTextVNode(" Close ", -1)]))]),
          _: 1
        }), _createVNode(LlSecondaryButton, {
          color: "primary",
          loading: templateState.saving,
          onBtnClick: saveTemplate
        }, {
          default: _withCtx(() => [...(_cache[25] || (_cache[25] = [_createTextVNode(" Save Template ", -1)]))]),
          _: 1
        }, 8, ["loading"])])])]))]),
        _: 1
      });
    };
  }
};